import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Badge } from 'components/ui/badge';
import { 
  ArrowLeft, Users, Plus, CheckCircle, Clock, Trash2,
  Mail, UserPlus, Calendar, AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import RecurringTasks from 'components/RecurringTasks';

export default function CareCircle() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole] = useState('caregiver');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '' });
  const [taskOpen, setTaskOpen] = useState(false);

  const [createCircleLoading, setCreateCircleLoading] = useState(false);
  const [userProfiles, setUserProfiles] = useState([]);

  useEffect(() => {
    fetchCircles();
    fetchUserProfiles();
  }, []);

  useEffect(() => {
    if (selectedCircle) {
      fetchTasks();
      fetchActivity();
    }
  }, [selectedCircle]);

  const fetchUserProfiles = async () => {
    try {
      const response = await api.get('/care-profiles');
      setUserProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
    }
  };

  const fetchCircles = async () => {
    try {
      const response = await api.get('/care-circles');
      setCircles(response.data.circles || []);
      if (response.data.circles?.length > 0) {
        setSelectedCircle(response.data.circles[0]);
      }
    } catch (error) {
      console.error('Failed to fetch circles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    if (!selectedCircle) return;
    try {
      const response = await api.get(`/care-circles/${selectedCircle.circle_id}/tasks`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchActivity = async () => {
    if (!selectedCircle) return;
    try {
      const response = await api.get(`/care-circles/${selectedCircle.circle_id}/activity`);
      setActivity(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail || !selectedCircle) return;
    try {
      await api.post(`/care-circles/${selectedCircle.circle_id}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      toast.success('Invitation sent!');
      setInviteEmail('');
      setInviteOpen(false);
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !selectedCircle) return;
    try {
      await api.post(`/care-circles/${selectedCircle.circle_id}/tasks`, newTask);
      toast.success('Task added!');
      setNewTask({ title: '', description: '' });
      setTaskOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/complete`, {});
      toast.success('Task updated!');
      fetchTasks();
      fetchActivity();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedCircle) return;
    try {
      await api.delete(`/care-circles/${selectedCircle.circle_id}/members/${memberId}`);
      toast.success('Member removed');
      fetchCircles();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleCreateCircle = async (profileId) => {
    if (!profileId) return;
    setCreateCircleLoading(true);
    try {
      const profile = userProfiles.find(p => p.profile_id === profileId);
      await api.post('/care-circles', {
        profile_id: profileId,
        name: `${profile?.care_recipient_name || 'Care'}'s Circle`
      });
      toast.success('Care Circle created!');
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create circle');
    } finally {
      setCreateCircleLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="care-circle">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Care Circle™</h1>
              <p className="text-xs sm:text-xs sm:text-sm text-slate-500">Coordinate with family and caregivers</p>
            </div>
          </div>
          {selectedCircle && user?.user_id === selectedCircle.owner_id && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full whitespace-nowrap" data-testid="invite-btn">
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Invite</span> Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite to Care Circle</DialogTitle>
                  <DialogDescription>Send an invitation to join your care circle.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      data-testid="invite-email-input"
                    />
                  </div>
                  <Button
                    onClick={handleInvite}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="send-invite-btn"
                  >
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {circles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Care Circle Yet</h2>
              {userProfiles.length > 0 ? (
                <>
                  <p className="text-slate-500 mb-6">Create a Care Circle to start managing tasks and coordinating care.</p>
                  <div className="space-y-3 max-w-md mx-auto">
                    {userProfiles.map(profile => (
                      <Button 
                        key={profile.profile_id}
                        onClick={() => handleCreateCircle(profile.profile_id)}
                        disabled={createCircleLoading}
                        className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                        data-testid="create-circle-btn"
                      >
                        {createCircleLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Create Circle for {profile.care_recipient_name}
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-500 mb-6">
                    {user?.role === 'independent_caregiver'
                      ? 'You need to be added to a care circle by a family caregiver.'
                      : 'Set up a care profile first to create your Care Circle'}
                  </p>
                  {user?.role !== 'independent_caregiver' && (
                    <Button className="bg-teal-500 hover:bg-teal-600 text-white" onClick={() => navigate('/care-profile/setup')}>
                      Create Care Profile
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Circle Info & Members */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">{selectedCircle?.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-2">Members</p>
                      <div className="space-y-2">
                        {selectedCircle?.members?.map((member, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                                <span className="text-teal-700 font-medium">
                                  {member.name?.[0] || member.email?.[0] || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {member.name || member.email}
                                </p>
                                <p className="text-xs text-slate-500 capitalize">
                                  {member.role?.replace('_', ' ')}
                                  {member.pending && ' (Pending)'}
                                </p>
                              </div>
                            </div>
                            {user?.user_id === selectedCircle.owner_id && member.user_id !== user.user_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-slate-400 hover:text-rose-600"
                                onClick={() => handleRemoveMember(member.email)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Tasks & Activity */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="tasks">
                <TabsList className="grid grid-cols-3 w-full mb-4">
                  <TabsTrigger value="tasks">Tasks</TabsTrigger>
                  <TabsTrigger value="recurring">Recurring</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="tasks" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-slate-900">Tasks</h3>
                    <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-task-btn">
                          <Plus className="w-4 h-4" />
                          Add Task
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add New Task</DialogTitle>
                          <DialogDescription>Create a new task for your care circle.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div className="space-y-2">
                            <Label>Task Title</Label>
                            <Input
                              placeholder="e.g., Give medication"
                              value={newTask.title}
                              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                              data-testid="task-title-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description (Optional)</Label>
                            <Input
                              placeholder="Additional details..."
                              value={newTask.description}
                              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                            />
                          </div>
                          <Button
                            onClick={handleAddTask}
                            className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                            data-testid="save-task-btn"
                          >
                            Add Task
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {tasks.length === 0 ? (
                    <Card className="text-center py-8">
                      <CardContent>
                        <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No tasks yet. Add your first task!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {tasks.map((task) => (
                        <Card key={task.task_id} className={task.completed ? 'opacity-60' : ''}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => handleCompleteTask(task.task_id)}
                                className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
                                  task.completed
                                    ? 'bg-teal-500 border-teal-500 hover:bg-teal-400'
                                    : 'border-slate-300 hover:border-teal-500'
                                }`}
                                data-testid={`complete-task-${task.task_id}`}
                              >
                                {task.completed && <CheckCircle className="w-4 h-4 text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className={`font-medium ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                  {task.title}
                                </p>
                                {task.description && (
                                  <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                                )}
                                {task.due_date && (
                                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                                    <Calendar className="w-3 h-3" />
                                    Due: {new Date(task.due_date).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                              {task.assigned_to && (
                                <Badge variant="secondary" className="text-xs">
                                  Assigned
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="recurring" className="space-y-4">
                  <RecurringTasks circleId={selectedCircle?.circle_id} />
                </TabsContent>

                <TabsContent value="activity" className="space-y-4">
                  <h3 className="font-semibold text-slate-900">Recent Activity</h3>
                  {activity.length === 0 ? (
                    <Card className="text-center py-8">
                      <CardContent>
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No activity yet</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {activity.map((item, index) => (
                        <div key={item.activity_id || index} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-100">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            item.action === 'completed_task' ? 'bg-teal-100' :
                            item.action === 'clocked_in' ? 'bg-green-100' :
                            item.action === 'clocked_out' ? 'bg-slate-100' :
                            'bg-blue-100'
                          }`}>
                            {item.action === 'completed_task' ? (
                              <CheckCircle className="w-4 h-4 text-teal-600" />
                            ) : item.action === 'clocked_in' || item.action === 'clocked_out' ? (
                              <Clock className="w-4 h-4 text-slate-600" />
                            ) : item.action === 'invited' ? (
                              <Mail className="w-4 h-4 text-blue-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-slate-900">
                              <span className="font-medium">{item.user_name}</span>
                              {' '}
                              {item.action === 'completed_task' ? `completed "${item.target}"` :
                               item.action === 'created_task' ? `added task "${item.target}"` :
                               item.action === 'clocked_in' ? 'clocked in' :
                               item.action === 'clocked_out' ? `clocked out (${item.target})` :
                               item.action === 'invited' ? `invited ${item.target}` :
                               item.message || item.action || item.type || 'performed an action'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(item.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
